import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Divider,
  CircularProgress,
} from '@mui/material';
import DashboardNavbar from 'components/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import { useEffect, useState } from 'react';
import { getAffiliateCodes } from 'services/affiliate-codes/get-affiliate-codes';
import { getAffiliateCodeById } from 'services/affiliate-codes/get-affiliate-code-by-id';
import { setAffiliateCodeOwner } from 'services/affiliate-codes/set-affiliate-code-owner';
import { setAffiliateCodesToUsers } from 'services/affiliate-codes/set-affiliate-codes-to-users';
import { getPurchasesByAffiliateCode } from 'services/affiliate-codes/get-purchases-by-affiliate-code';
import { getAllPlayers } from 'services/filters';
import { connectUserAffiliateCodeAdmin } from 'services/affiliate-codes/connect-user-affiliate-code';

export default function AffiliateCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCode, setSelectedCode] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [openSetOwner, setOpenSetOwner] = useState(false);
  const [openBulkSetOwner, setOpenBulkSetOwner] = useState(false);
  const [openPurchases, setOpenPurchases] = useState(false);
  const [codeToEdit, setCodeToEdit] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [bulkUserIds, setBulkUserIds] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [openConnectUser, setOpenConnectUser] = useState(false);
  const [connectUserId, setConnectUserId] = useState('');
  const [connectAffiliateCode, setConnectAffiliateCode] = useState('');
  const [connecting, setConnecting] = useState(false);

  async function initData() {
    setLoading(true);
    try {
      const res = await getAffiliateCodes();
      if (res?.data) {
        setCodes(res.data);
      }
    } catch (error) {
      console.error('Error fetching affiliate codes:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    initData();
  }, []);

  const handleViewDetails = async (id) => {
    try {
      const res = await getAffiliateCodeById(id);
      if (res?.data) {
        setSelectedCode(res.data);
        setOpenDetails(true);
      }
    } catch (error) {
      console.error('Error fetching affiliate code details:', error);
      alert('Error loading code details');
    }
  };

  const handleSetOwner = (code) => {
    setCodeToEdit(code);
    setSelectedOwner(code.ownedBy ? { id: code.ownedBy.id, label: code.ownedBy.nickname || code.ownedBy.email || code.ownedBy.id } : null);
    setOpenSetOwner(true);
  };

  const handleSaveOwner = async () => {
    if (!codeToEdit) return;
    
    try {
      await setAffiliateCodeOwner(codeToEdit.id, selectedOwner?.id || null);
      alert('Owner updated successfully');
      setOpenSetOwner(false);
      setCodeToEdit(null);
      setSelectedOwner(null);
      initData(); // Refresh list
    } catch (error) {
      console.error('Error setting owner:', error);
      alert(error?.response?.data?.message || 'Error updating owner');
    }
  };

  const handleBulkSetOwner = () => {
    setBulkUserIds([]);
    setOpenBulkSetOwner(true);
  };

  const handleBulkSaveOwner = async () => {
    if (bulkUserIds.length === 0) {
      alert('Please select at least one user');
      return;
    }

    try {
      const userIds = bulkUserIds.map(u => u.id);
      await setAffiliateCodesToUsers(userIds);
      alert(`Successfully assigned codes to ${bulkUserIds.length} user(s)`);
      setOpenBulkSetOwner(false);
      setBulkUserIds([]);
      initData(); // Refresh list
    } catch (error) {
      console.error('Error setting owners:', error);
      alert(error?.response?.data?.message || 'Error assigning codes');
    }
  };

  const handleViewPurchases = async (code) => {
    setSelectedCode(code);
    setLoadingPurchases(true);
    setOpenPurchases(true);
    try {
      const res = await getPurchasesByAffiliateCode(code.code);
      if (res?.data) {
        setPurchases(res.data);
      } else {
        setPurchases([]);
      }
    } catch (error) {
      console.error('Error fetching purchases:', error);
      setPurchases([]);
    } finally {
      setLoadingPurchases(false);
    }
  };

  const handleConnectUser = async () => {
    if (!connectUserId.trim() || !connectAffiliateCode.trim()) {
      alert('Please enter both User ID and Affiliate Code');
      return;
    }

    setConnecting(true);
    try {
      const res = await connectUserAffiliateCodeAdmin(connectUserId.trim(), connectAffiliateCode.trim().toUpperCase());
      if (res?.data?.success) {
        alert('User successfully connected with affiliate code');
        setOpenConnectUser(false);
        setConnectUserId('');
        setConnectAffiliateCode('');
        initData(); // Refresh affiliate codes list
      } else {
        alert(res?.response?.data?.message || 'Failed to connect user with affiliate code');
      }
    } catch (error) {
      console.error('Error connecting user with affiliate code:', error);
      alert(error?.response?.data?.message || 'Error connecting user with affiliate code');
    } finally {
      setConnecting(false);
    }
  };

  const handleSearchUsers = async (searchTerm) => {
    // Handle both string and event object inputs
    const searchValue = typeof searchTerm === 'string' ? searchTerm : searchTerm?.target?.value || '';
    
    if (!searchValue || searchValue.length < 2) {
      setUsers([]);
      return;
    }

    setLoadingUsers(true);
    try {
      const userList = await getAllPlayers(searchValue, false, false);
      // Ensure userList is always an array
      const safeUserList = Array.isArray(userList) ? userList : [];
      setUsers(safeUserList);
    } catch (error) {
      console.error('Error searching users:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
    setSelectedCode(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  const getUsersCount = (code) => {
    return code.usersWhoUsed?.length || 0;
  };

  const getTotalPurchasesValue = () => {
    return purchases.reduce((sum, purchase) => sum + (purchase.price || 0), 0);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <MDTypography variant='h4' fontWeight='bold' color='dark'>
              Affiliate Codes
            </MDTypography>
            <MDBox display='flex' gap={1.5}>
              <Button
                variant='outlined'
                color='primary'
                onClick={handleBulkSetOwner}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  color: 'dark.main',
                  borderColor: 'dark.main',
                  '&:hover': {
                    borderColor: 'dark.main',
                    backgroundColor: 'grey.100',
                  },
                }}
              >
                Bulk Assign Codes
              </Button>
              <Button
                variant='contained'
                color='success'
                onClick={() => setOpenConnectUser(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              >
                Connect User with Code
              </Button>
            </MDBox>
          </MDBox>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
              <MDBox p={3}>
                {loading ? (
                  <MDBox py={4} textAlign='center'>
                    <CircularProgress size={40} />
                    <MDTypography variant='body1' color='text' mt={2}>
                      Loading affiliate codes...
                    </MDTypography>
                  </MDBox>
                ) : codes.length === 0 ? (
                  <MDBox py={4} textAlign='center'>
                    <MDTypography variant='body1' color='text'>
                      No affiliate codes found
                    </MDTypography>
                  </MDBox>
                ) : (
                  <TableContainer>
                    <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                      <colgroup>
                        <col style={{ width: '80px' }} />
                        <col style={{ width: '220px' }} />
                        <col style={{ width: '200px' }} />
                        <col style={{ width: '160px' }} />
                        <col style={{ width: '130px' }} />
                        <col style={{ width: '200px' }} />
                        <col style={{ width: 'auto' }} />
                      </colgroup>
                      <TableBody>
<TableRow sx={{ backgroundColor: 'grey.100' }}>
                          <TableCell align='left' sx={{ width: '80px', padding: '16px 12px' }}>
                            <MDTypography variant='body2' fontWeight='bold' color='dark'>
                              ID
                            </MDTypography>
                          </TableCell>
                          <TableCell align='left' sx={{ width: '220px', padding: '16px 12px' }}>
                            <MDTypography variant='body2' fontWeight='bold' color='dark'>
                              Code
                            </MDTypography>
                          </TableCell>
                          <TableCell align='left' sx={{ width: '200px', padding: '16px 12px' }}>
                            <MDTypography variant='body2' fontWeight='bold' color='dark'>
                              Owner
                            </MDTypography>
                          </TableCell>
                          <TableCell align='left' sx={{ width: '160px', padding: '16px 12px' }}>
                            <MDTypography variant='body2' fontWeight='bold' color='dark'>
                              Users Who Used
                            </MDTypography>
                          </TableCell>
                          <TableCell align='left' sx={{ width: '130px', padding: '16px 12px' }}>
                            <MDTypography variant='body2' fontWeight='bold' color='dark'>
                              Status
                            </MDTypography>
                          </TableCell>
                          <TableCell align='left' sx={{ width: '200px', padding: '16px 12px' }}>
                            <MDTypography variant='body2' fontWeight='bold' color='dark'>
                              Created At
                            </MDTypography>
                          </TableCell>
                          <TableCell align='left' sx={{ padding: '16px 12px' }}>
                            <MDTypography variant='body2' fontWeight='bold' color='dark'>
                              Actions
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                        {codes.map((code, index) => (
                          <TableRow 
                            key={code.id}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'grey.50',
                              },
                              backgroundColor: index % 2 === 0 ? 'white' : 'grey.25',
                            }}
                          >
                            <TableCell align='left' sx={{ width: '80px', padding: '16px 12px' }}>
                              <MDTypography variant='body2' fontWeight='medium'>
                                {code.id}
                              </MDTypography>
                            </TableCell>
                            <TableCell align='left' sx={{ width: '220px', padding: '16px 12px' }}>
                              <MDTypography 
                                variant='body2' 
                                fontWeight='bold' 
                                color='primary'
                                sx={{ fontFamily: 'monospace' }}
                              >
                                {code.code}
                              </MDTypography>
                            </TableCell>
                            <TableCell align='left' sx={{ width: '200px', padding: '16px 12px' }}>
                              {code.ownedBy ? (
                                <MDTypography variant='body2' fontWeight='medium'>
                                  {code.ownedBy.nickname || code.ownedBy.email || code.ownedBy.id}
                                </MDTypography>
                              ) : (
                                <MDTypography variant='body2' color='textSecondary' fontStyle='italic'>
                                  Unassigned
                                </MDTypography>
                              )}
                            </TableCell>
                            <TableCell align='left' sx={{ width: '160px', padding: '16px 12px' }}>
                              <MDTypography variant='body2' color='text'>
                                {getUsersCount(code)} user(s)
                              </MDTypography>
                            </TableCell>
                            <TableCell align='left' sx={{ width: '130px', padding: '16px 12px' }}>
                              <Chip
                                label={code.isDeleted ? 'Deleted' : code.ownedBy ? 'Assigned' : 'Available'}
                                color={code.isDeleted ? 'error' : code.ownedBy ? 'info' : 'success'}
                                size='small'
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                            </TableCell>
                            <TableCell align='left' sx={{ width: '200px', padding: '16px 12px' }}>
                              <MDTypography variant='body2' color='text'>
                                {formatDate(code.createdAt)}
                              </MDTypography>
                            </TableCell>
                            <TableCell align='left' sx={{ padding: '16px 12px' }}>
                              <Box display='flex' gap={0.5} flexWrap='wrap'>
                                <Button
                                  variant='outlined'
                                  size='small'
                                  onClick={() => handleViewDetails(code.id)}
                                  sx={{
                                    textTransform: 'none',
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                    color: 'dark.main',
                                    borderColor: 'grey.400',
                                    '&:hover': {
                                      borderColor: 'dark.main',
                                      backgroundColor: 'grey.100',
                                    },
                                  }}
                                >
                                  Details
                                </Button>
                                <Button
                                  variant='outlined'
                                  size='small'
                                  onClick={() => handleSetOwner(code)}
                                  sx={{
                                    textTransform: 'none',
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                    color: 'dark.main',
                                    borderColor: 'grey.400',
                                    '&:hover': {
                                      borderColor: 'dark.main',
                                      backgroundColor: 'grey.100',
                                    },
                                  }}
                                >
                                  Set Owner
                                </Button>
                                <Button
                                  variant='outlined'
                                  size='small'
                                  onClick={() => handleViewPurchases(code)}
                                  sx={{
                                    textTransform: 'none',
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                    color: 'dark.main',
                                    borderColor: 'grey.400',
                                    '&:hover': {
                                      borderColor: 'dark.main',
                                      backgroundColor: 'grey.100',
                                    },
                                  }}
                                >
                                  Purchases
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Details Dialog */}
      <Dialog open={openDetails} onClose={handleCloseDetails} maxWidth='md' fullWidth>
        <DialogTitle>Affiliate Code Details</DialogTitle>
        <DialogContent>
          {selectedCode && (
            <MDBox py={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <MDTypography variant='h6' fontWeight='bold' mb={1}>
                    Code Information
                  </MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant='body2' color='textSecondary'>
                    ID:
                  </MDTypography>
                  <MDTypography variant='body1'>{selectedCode.id}</MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant='body2' color='textSecondary'>
                    Code:
                  </MDTypography>
                  <MDTypography variant='body1' fontWeight='bold'>
                    {selectedCode.code}
                  </MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant='body2' color='textSecondary'>
                    Status:
                  </MDTypography>
                  <Chip
                    label={selectedCode.isDeleted ? 'Deleted' : selectedCode.ownedBy ? 'Assigned' : 'Available'}
                    color={selectedCode.isDeleted ? 'error' : selectedCode.ownedBy ? 'info' : 'success'}
                    size='small'
                  />
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant='body2' color='textSecondary'>
                    Created At:
                  </MDTypography>
                  <MDTypography variant='body1'>
                    {formatDate(selectedCode.createdAt)}
                  </MDTypography>
                </Grid>
                {selectedCode.ownedBy && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <MDTypography variant='h6' fontWeight='bold' mb={1}>
                        Owner Information
                      </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <MDTypography variant='body2' color='textSecondary'>
                        User ID:
                      </MDTypography>
                      <MDTypography variant='body1'>
                        {selectedCode.ownedBy.id}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <MDTypography variant='body2' color='textSecondary'>
                        Email:
                      </MDTypography>
                      <MDTypography variant='body1'>
                        {selectedCode.ownedBy.email || 'N/A'}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <MDTypography variant='body2' color='textSecondary'>
                        Nickname:
                      </MDTypography>
                      <MDTypography variant='body1'>
                        {selectedCode.ownedBy.nickname || 'N/A'}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <MDTypography variant='body2' color='textSecondary'>
                        Is Affiliate Code Owner:
                      </MDTypography>
                      <Chip
                        label={selectedCode.ownedBy.isAffiliateCodeOwner ? 'Yes' : 'No'}
                        color={selectedCode.ownedBy.isAffiliateCodeOwner ? 'success' : 'default'}
                        size='small'
                      />
                    </Grid>
                  </>
                )}
                {selectedCode.usersWhoUsed && selectedCode.usersWhoUsed.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <MDTypography variant='h6' fontWeight='bold' mb={1}>
                        Users Who Used This Code ({selectedCode.usersWhoUsed.length})
                      </MDTypography>
                    </Grid>
                    {selectedCode.usersWhoUsed.map((usage, index) => (
                      <Grid item xs={12} key={usage.id || index}>
                        <MDBox p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <MDTypography variant='body2' color='textSecondary'>
                            User: {usage.user?.nickname || usage.user?.email || usage.user?.id || 'N/A'}
                          </MDTypography>
                          <MDTypography variant='body2' color='textSecondary'>
                            Used At: {formatDate(usage.usedAt)}
                          </MDTypography>
                          <MDBox mt={1} display='flex' gap={1}>
                            <Chip
                              label={usage.user?.hasAffiliateAccess ? 'Has Affiliate Access' : 'No Affiliate Access'}
                              color={usage.user?.hasAffiliateAccess ? 'success' : 'default'}
                              size='small'
                            />
                          </MDBox>
                        </MDBox>
                      </Grid>
                    ))}
                  </>
                )}
              </Grid>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Set Owner Dialog */}
      <Dialog open={openSetOwner} onClose={() => { setOpenSetOwner(false); setCodeToEdit(null); setSelectedOwner(null); }} maxWidth='sm' fullWidth>
        <DialogTitle>Set Affiliate Code Owner</DialogTitle>
        <DialogContent>
          <MDBox py={2}>
            {codeToEdit && (
              <MDTypography variant='body2' color='textSecondary' mb={2}>
                Code: <strong>{codeToEdit.code}</strong>
              </MDTypography>
            )}
            <Autocomplete
              options={Array.isArray(users) ? users : []}
              value={selectedOwner}
              onChange={(event, newValue) => setSelectedOwner(newValue)}
              onInputChange={(event, newInputValue) => {
                if (newInputValue && typeof newInputValue === 'string') {
                  handleSearchUsers(newInputValue);
                } else if (!newInputValue) {
                  setUsers([]);
                }
              }}
              loading={loadingUsers}
              getOptionLabel={(option) => option?.label || option?.nickname || option?.email || option?.id || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Search User'
                  placeholder='Type to search users...'
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? <CircularProgress color='inherit' size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <MDTypography variant='caption' color='textSecondary' mt={1} display='block'>
              Leave empty to remove owner
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenSetOwner(false); setCodeToEdit(null); setSelectedOwner(null); }}>
            Cancel
          </Button>
          <Button onClick={handleSaveOwner} variant='contained' color='primary'>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Set Owner Dialog */}
      <Dialog open={openBulkSetOwner} onClose={() => { setOpenBulkSetOwner(false); setBulkUserIds([]); }} maxWidth='sm' fullWidth>
        <DialogTitle>Bulk Assign Codes to Users</DialogTitle>
        <DialogContent>
          <MDBox py={2}>
            <MDTypography variant='body2' color='textSecondary' mb={2}>
              Select users to assign affiliate codes. Codes will be assigned from oldest unassigned codes.
            </MDTypography>
            <Autocomplete
              multiple
              options={Array.isArray(users) ? users : []}
              value={bulkUserIds}
              onChange={(event, newValue) => setBulkUserIds(newValue)}
              onInputChange={(event, newInputValue) => {
                if (newInputValue && typeof newInputValue === 'string') {
                  handleSearchUsers(newInputValue);
                } else if (!newInputValue) {
                  setUsers([]);
                }
              }}
              loading={loadingUsers}
              getOptionLabel={(option) => option?.label || option?.nickname || option?.email || option?.id || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Search Users'
                  placeholder='Type to search users...'
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? <CircularProgress color='inherit' size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenBulkSetOwner(false); setBulkUserIds([]); }}>
            Cancel
          </Button>
          <Button onClick={handleBulkSaveOwner} variant='contained' color='primary'>
            Assign Codes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purchases Dialog */}
      <Dialog open={openPurchases} onClose={() => { setOpenPurchases(false); setPurchases([]); setSelectedCode(null); }} maxWidth='lg' fullWidth>
        <DialogTitle>
          Purchases for Code: {selectedCode?.code}
        </DialogTitle>
        <DialogContent>
          <MDBox py={2}>
            {loadingPurchases ? (
              <Box display='flex' justifyContent='center' p={3}>
                <CircularProgress />
              </Box>
            ) : purchases.length === 0 ? (
              <MDTypography>No purchases found for this affiliate code</MDTypography>
            ) : (
              <>
                <MDBox mb={2}>
                  <MDTypography variant='h6' fontWeight='bold'>
                    Total Purchases: {purchases.length}
                  </MDTypography>
                  <MDTypography variant='body2' color='textSecondary'>
                    Total Value: {formatCurrency(getTotalPurchasesValue())}
                  </MDTypography>
                </MDBox>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Asset Type</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Currency</TableCell>
                        <TableCell>Payment Provider</TableCell>
                        <TableCell>Purchased At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchases.map((purchase) => (
                        <TableRow key={purchase.id}>
                          <TableCell>
                            {purchase.user?.nickname || purchase.user?.email || purchase.user?.id || 'N/A'}
                          </TableCell>
                          <TableCell>{purchase.assetType || 'N/A'}</TableCell>
                          <TableCell>{formatCurrency(purchase.price, purchase.currency)}</TableCell>
                          <TableCell>{purchase.currency || 'N/A'}</TableCell>
                          <TableCell>{purchase.paymentProvider || 'N/A'}</TableCell>
                          <TableCell>{formatDate(purchase.purchasedAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenPurchases(false); setPurchases([]); setSelectedCode(null); }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Connect User with Affiliate Code Dialog */}
      <Dialog 
        open={openConnectUser} 
        onClose={() => { setOpenConnectUser(false); setConnectUserId(''); setConnectAffiliateCode(''); }} 
        maxWidth='sm' 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 4,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant='h5' fontWeight='bold' color='dark'>
            Connect User with Affiliate Code
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox py={2}>
            <TextField
              fullWidth
              label='User ID'
              placeholder='Enter user ID (UUID)'
              value={connectUserId}
              onChange={(e) => setConnectUserId(e.target.value)}
              disabled={connecting}
              sx={{ mb: 2.5 }}
              variant='outlined'
            />
            <TextField
              fullWidth
              label='Affiliate Code'
              placeholder='Enter affiliate code (e.g., AFFILIATE-XXXXX)'
              value={connectAffiliateCode}
              onChange={(e) => setConnectAffiliateCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !connecting && connectUserId.trim() && connectAffiliateCode.trim()) {
                  handleConnectUser();
                }
              }}
              disabled={connecting}
              sx={{ mb: 2 }}
              variant='outlined'
            />
            <MDTypography variant='caption' color='textSecondary' sx={{ fontStyle: 'italic' }}>
              This will set hasAffiliateAccess = true for the user and record their usage of the affiliate code.
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button 
            onClick={() => { setOpenConnectUser(false); setConnectUserId(''); setConnectAffiliateCode(''); }} 
            disabled={connecting}
            sx={{ textTransform: 'none', px: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConnectUser} 
            variant='contained' 
            color='primary' 
            disabled={connecting || !connectUserId.trim() || !connectAffiliateCode.trim()}
            sx={{ 
              textTransform: 'none', 
              px: 3,
              borderRadius: 1.5,
              fontWeight: 600,
            }}
          >
            {connecting ? 'Connecting...' : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
